import { forwardRef, memo, type KeyboardEvent } from 'react';
import clsx from 'clsx';
import { Checkbox } from '@headlessui/react';

import { SelectOption } from '~/shared/utils/types';
import Tag from '~/shared/components/Tag';
import Typography from '~/shared/components/Typography';
import CheckIcon from '~/shared/assets/icons/check.svg';

interface FleetSearchFilterOptionRowProps {
  option: SelectOption;
  onSelect: (option: SelectOption) => void;
  label?: never;
  count?: never;
  color?: never;
  indented?: never;
  indeterminate?: never;
  onToggle?: never;
}

interface ExplorationSearchFilterOptionRowProps {
  option?: never;
  onSelect?: never;
  label: string;
  count?: number;
  color?: string;
  indented?: boolean;
  indeterminate?: boolean;
  onToggle: () => void;
}

type SearchFilterOptionRowProps = {
  isSelected: boolean;
  onHover?: () => void;
  className?: string;
  id?: string;
  tagVariant?: 'cluster' | 'instance' | 'volume' | 'node' | 'default';
  isFocused?: boolean;
  tabIndex?: number;
  onKeyDown?: (e: KeyboardEvent<HTMLDivElement>) => void;
} & (FleetSearchFilterOptionRowProps | ExplorationSearchFilterOptionRowProps);

const SearchFilterOptionRow = memo(
  forwardRef<HTMLDivElement, SearchFilterOptionRowProps>(function SearchFilterOptionRow(
    {
      option,
      isSelected,
      onSelect,
      onHover,
      className,
      id,
      tagVariant = 'default',
      isFocused = false,
      label,
      count,
      color,
      indented = false,
      indeterminate = false,
      onToggle,
      tabIndex = -1,
      onKeyDown,
    },
    ref
  ) {
    const isExplorationMode = label != null;
    const showChecked = isSelected && !indeterminate;
    const explorationAriaLabel =
      isExplorationMode && count != null
        ? `${label}, ${count.toLocaleString()} volumes`
        : isExplorationMode
          ? label
          : undefined;

    const handleActivate = () => {
      if (isExplorationMode) {
        onToggle?.();
      } else if (option && onSelect) {
        onSelect(option);
      }
    };

    return (
      <div
        ref={ref}
        id={id}
        className={clsx(
          'p-3 my-1 rounded-lg transition-colors flex items-center cursor-pointer outline-none',
          indented && 'pl-8',
          isFocused && 'bg-primary-20',
          className
        )}
        role={isExplorationMode ? 'checkbox' : undefined}
        aria-label={explorationAriaLabel}
        aria-checked={isExplorationMode ? (indeterminate ? 'mixed' : isSelected) : undefined}
        tabIndex={tabIndex}
        onClick={handleActivate}
        onMouseEnter={onHover}
        onKeyDown={(e) => {
          onKeyDown?.(e);
          if (e.defaultPrevented) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleActivate();
          }
        }}
      >
        <div className="mr-2 shrink-0">
          <Checkbox
            checked={showChecked}
            onChange={handleActivate}
            className={clsx(
              'group block size-5 p-[4px] rounded-md border bg-white ring:0 active:ring-primary-300 focus:ring-primary-500 cursor-pointer',
              indeterminate
                ? 'border-primary-500'
                : 'border-gray-400 data-checked:bg-primary-500 data-checked:border-primary-500',
              isFocused && !indeterminate && 'border-primary-400'
            )}
            onClick={(e) => e.stopPropagation()}
          >
            {!indeterminate && (
              <CheckIcon className="text-white stroke-2 size-2.5 opacity-0 group-data-checked:opacity-100 transition duration-200 ease-in-out" />
            )}
          </Checkbox>
        </div>

        {isExplorationMode ? (
          <span className="flex flex-1 min-w-0 items-center gap-1">
            {tagVariant !== 'default' ? (
              <Tag variant={tagVariant} size="sm" text={label} truncateToContainer />
            ) : (
              <>
                {color ? (
                  <span
                    className="size-[11px] rounded-sm shrink-0"
                    style={{ background: color }}
                    aria-hidden
                  />
                ) : null}
                <Typography variant="p3-reg" className="truncate text-primary-900">
                  {label}
                </Typography>
              </>
            )}
            {count != null ? (
              <Typography
                variant="caption"
                className="text-gray-600 tabular-nums shrink-0 ml-auto"
              >
                {count.toLocaleString()}
              </Typography>
            ) : null}
          </span>
        ) : (
          option && (
            <Tag
              variant={tagVariant}
              size="md"
              text={option.name}
              className="cursor-pointer"
              truncateToContainer
            />
          )
        )}
      </div>
    );
  })
);

export default SearchFilterOptionRow;
